import {
    Dashboard as DashboardIcon,
    Person as PersonIcon,
    LocalShipping as ShippingIcon,
    People as PeopleIcon,
    Assessment as AssessmentIcon,
    Settings as SettingsIcon,
    Business as BusinessIcon,
    History as HistoryIcon,
    AttachMoney as MoneyIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Hub as ZoneIcon,
    Public as RegionIcon,
    BookOnline as BookingIcon,
    TrackChanges as TrackingIcon,
    Receipt as InvoiceIcon,
    SupervisorAccount as UserManagementIcon,
    Groups as CustomersIcon,
    Store as FranchiseIcon,
    Inventory as ProductIcon,
    LocationOn as LocationIcon,
    BarChart as ReportsIcon,
    Search as TrackIcon,
    AccountCircle as ProfileIcon,
    Tune as AdvancedSettingsIcon,
    Timeline as ActivityIcon,
    Assignment as DeliveryRunSheetIcon,
    Assignment as AssignmentIcon,
    CheckCircle as CheckCircleIcon,
    MarkEmailRead as EmailSettingsIcon,
    ReceiptLong as StatementIcon,
    Payments as CollectionsIcon,
    TrendingUp as RevenueIcon,
    Leaderboard as RankingIcon,
    ListAlt as RegisterIcon,
    LocalShipping as PendingDeliveryIcon,
    FactCheck as DeliveryPerformanceIcon,
    ReportProblem as DataQualityIcon,
    RequestQuote as GstIcon,
    AccountBalanceWallet as PendingPaymentsIcon,
} from '@mui/icons-material';
import { regionsAreUsed } from './rateEngine';
import authService from './authService';

// Define all possible menu items with their required permissions
const allMenuItems = {
    employee: [
        {
            text: 'Dashboard',
            path: '/employee/dashboard',
            icon: <DashboardIcon />,
            permission: null, // No permission required for dashboard
            category: 'main'
        },
        {
            text: 'Booking Management',
            path: '/employee/bookings',
            icon: <BookingIcon />,
            permission: 'view-bookings',
            category: 'bookings'
        },
        {
            text: 'Tracking Management',
            path: '/employee/trackings',
            icon: <TrackingIcon />,
            permission: null, // Temporarily remove permission requirement for testing
            category: 'bookings'
        },
        {
            text: 'Transit Planner',
            path: '/employee/transit-planner',
            icon: <TrackingIcon />,
            permission: 'view-bookings',
            category: 'bookings'
        },
        {
            text: 'Delivery Run Sheet',
            path: '/employee/delivery-run-sheet',
            icon: <DeliveryRunSheetIcon />,
            permission: 'view-bookings',
            category: 'bookings'
        },
        {
            text: 'DRS Management',
            path: '/employee/drs-list',
            icon: <AssignmentIcon />,
            permission: 'view-bookings',
            category: 'bookings'
        },
        {
            text: 'Delivery Confirmation',
            path: '/employee/delivery-confirmation',
            icon: <CheckCircleIcon />,
            permission: 'view-bookings',
            category: 'bookings'
        },
        {
            text: 'Invoice Management',
            path: '/employee/invoices',
            icon: <InvoiceIcon />,
            permission: 'view-invoices',
            category: 'financial'
        },
        {
            text: 'User Management',
            path: '/employee/user-management',
            icon: <UserManagementIcon />,
            permission: 'manage-users',
            category: 'user_management'
        },
        // Reports: one entry per report. The page loads each definition from
        // /reports/catalog, which is also where access is enforced.
        {
            text: 'Booking Register',
            path: '/employee/reports/booking-register',
            icon: <RegisterIcon />,
            permission: 'view-reports',
            category: 'reports'
        },
        {
            text: 'Pending Deliveries',
            path: '/employee/reports/pending-delivery',
            icon: <PendingDeliveryIcon />,
            permission: 'view-reports',
            category: 'reports'
        },
        {
            text: 'Pending Payments',
            path: '/employee/reports/pending-payments',
            icon: <PendingPaymentsIcon />,
            permission: 'view-finance-reports',
            category: 'reports'
        },
        {
            text: 'GST Reports',
            path: '/employee/reports/gst',
            icon: <GstIcon />,
            permission: 'view-reports',
            category: 'reports'
        },
        {
            text: 'Delivery Performance',
            path: '/employee/reports/delivery-performance',
            icon: <DeliveryPerformanceIcon />,
            permission: 'view-reports',
            category: 'reports',
            // Shown once the courier agent app is live; the report is hidden
            // on the server too (DeliveryPerformanceReport::hidden)
            hidden: true
        },
        {
            text: 'Revenue Analysis',
            path: '/employee/reports/revenue-analysis',
            icon: <RevenueIcon />,
            permission: 'view-reports',
            category: 'reports'
        },
        {
            text: 'Customer Analysis',
            path: '/employee/reports/customer-analysis',
            icon: <RankingIcon />,
            permission: 'view-reports',
            category: 'reports'
        },
        {
            text: 'Customer Statement',
            path: '/employee/reports/customer-statement',
            icon: <StatementIcon />,
            permission: 'view-finance-reports',
            category: 'reports'
        },
        {
            text: 'Collections Register',
            path: '/employee/reports/collections',
            icon: <CollectionsIcon />,
            permission: 'view-finance-reports',
            category: 'reports'
        },
        {
            text: 'Data Quality Exceptions',
            path: '/employee/reports/data-quality',
            icon: <DataQualityIcon />,
            permission: 'view-reports',
            category: 'reports'
        },
        {
            text: 'Customers',
            path: '/employee/customers',
            icon: <CustomersIcon />,
            permission: 'manage-users',
            category: 'user_management'
        },
        {
            text: 'Franchises',
            path: '/employee/franchises',
            icon: <FranchiseIcon />,
            permission: 'manage-users',
            category: 'user_management'
        },
        {
            text: 'Branches',
            path: '/employee/branches',
            icon: <BusinessIcon />,
            permission: 'manage-users',
            category: 'user_management'
        },
        {
            text: 'Product Management',
            path: '/employee/products',
            icon: <ProductIcon />,
            permission: 'manage-users',
            category: 'product_management'
        },
        {
            text: 'Location Management',
            path: '/employee/location-management',
            icon: <LocationIcon />,
            permission: 'manage-users',
            category: 'product_management'
        },
        {
            text: 'Zone Management',
            path: '/employee/zones',
            icon: <ZoneIcon />,
            permission: 'manage-users',
            category: 'product_management'
        },
        {
            text: 'Region Management',
            path: '/employee/regions',
            icon: <RegionIcon />,
            permission: 'manage-users',
            category: 'product_management'
        },
        {
            text: 'Activity Management',
            path: '/employee/activities',
            icon: <ActivityIcon />,
            permission: 'manage-users',
            category: 'product_management'
        },
        {
            text: 'Vehicle Management',
            path: '/employee/vehicles',
            icon: <ShippingIcon />,
            permission: 'manage-users',
            category: 'product_management'
        },
        {
            text: 'Email Settings',
            path: '/employee/settings/email',
            icon: <EmailSettingsIcon />,
            permission: 'manage-settings',
            category: 'settings'
        }
    ],
    customer: [
        {
            text: 'Dashboard',
            path: '/customer/dashboard',
            icon: <DashboardIcon />,
            permission: null,
            category: 'main'
        },
        {
            text: 'My Shipments',
            path: '/customer/shipments',
            icon: <ShippingIcon />,
            permission: null,
            category: 'shipments'
        },
        {
            text: 'Track Shipment',
            path: '/customer/track',
            icon: <TrackIcon />,
            permission: null,
            category: 'shipments'
        },
        {
            text: 'My Invoices',
            path: '/customer/invoices',
            icon: <InvoiceIcon />,
            permission: null,
            category: 'financial'
        }
    ],
    franchise: [
        {
            text: 'Dashboard',
            path: '/franchise/dashboard',
            icon: <DashboardIcon />,
            permission: null,
            category: 'main'
        },
        {
            text: 'Franchise Profile',
            path: '/franchise/profile',
            icon: <ProfileIcon />,
            permission: null,
            category: 'profile'
        },
        {
            text: 'Shipments',
            path: '/franchise/shipments',
            icon: <ShippingIcon />,
            permission: null,
            category: 'shipments'
        },
        {
            text: 'My Customers',
            path: '/franchise/customers',
            icon: <CustomersIcon />,
            permission: null,
            category: 'management'
        },
        {
            text: 'Reports',
            path: '/franchise/reports',
            icon: <ReportsIcon />,
            permission: null,
            category: 'reports'
        },
        {
            text: 'Revenue',
            path: '/franchise/revenue',
            icon: <MoneyIcon />,
            permission: null,
            category: 'reports'
        },
        {
            text: 'Settings',
            path: '/franchise/settings',
            icon: <AdvancedSettingsIcon />,
            permission: null,
            category: 'settings'
        }
    ]
};

// Menu categories for grouping
const menuCategories = {
    main: 'Main',
    bookings: 'Booking Management',
    financial: 'Financial',
    user_management: 'User Management',
    product_management: 'Product Management',
    reports: 'Reports & Analytics',
    shipments: 'Shipments',
    profile: 'Profile',
    settings: 'Settings',
    management: 'Management'
};

/**
 * Whether a menu item's permission is granted. Administrators pass every
 * check, as they do in authService.hasPermission, so a permission added after
 * they logged in does not hide screens until they log in again.
 */
const isGranted = (permission, userPermissions) =>
    !permission || authService.isAdmin() || userPermissions.includes(permission);

class MenuService {
    /**
     * Get filtered menu items based on user type and permissions
     */
    getMenuItems(userType, userPermissions = []) {
        const items = allMenuItems[userType] || [];

        return items.filter(item => {
            if (item.hidden) {
                return false;
            }

            // Regions no longer take part in rating under the zone engine, so
            // showing the screen would invite configuring something nothing reads.
            if (item.path === '/employee/regions' && !regionsAreUsed()) {
                return false;
            }

            return isGranted(item.permission, userPermissions);
        });
    }

    /**
     * Get grouped menu items by category
     */
    getGroupedMenuItems(userType, userPermissions = []) {
        const items = this.getMenuItems(userType, userPermissions);
        const grouped = {};

        items.forEach(item => {
            const category = item.category || 'main';
            if (!grouped[category]) {
                grouped[category] = {
                    title: menuCategories[category] || category,
                    items: []
                };
            }
            grouped[category].items.push(item);
        });

        // Define the desired order of categories, with reports at the end
        const categoryOrder = [
            'main',
            'bookings', 
            'financial',
            'user_management',
            'product_management',
            'shipments',
            'profile',
            'management',
            'settings',
            'reports' // Reports & Analytics at the bottom
        ];

        // Reorder the grouped items according to the defined order
        const orderedGrouped = {};
        categoryOrder.forEach(category => {
            if (grouped[category]) {
                orderedGrouped[category] = grouped[category];
            }
        });

        // Add any remaining categories that weren't in the predefined order
        Object.keys(grouped).forEach(category => {
            if (!orderedGrouped[category]) {
                orderedGrouped[category] = grouped[category];
            }
        });

        return orderedGrouped;
    }

    /**
     * Check if user has access to a specific path
     */
    hasAccessToPath(path, userType, userPermissions = []) {
        const items = allMenuItems[userType] || [];
        
        // First try exact match
        const exactMatch = items.find(item => item.path === path);
        if (exactMatch) {
            return isGranted(exactMatch.permission, userPermissions);
        }
        
        // Handle dynamic routes by checking base paths
        // For routes like /employee/invoices/:id and /employee/invoices/:id/print
        // Check if user has access to the base /employee/invoices route
        const pathSegments = path.split('/').filter(Boolean);
        
        // Try to find a base route that matches
        for (let i = pathSegments.length; i > 0; i--) {
            const basePath = '/' + pathSegments.slice(0, i).join('/');
            const baseMatch = items.find(item => item.path === basePath);
            
            if (baseMatch) {
                return isGranted(baseMatch.permission, userPermissions);
            }
        }
        
        // If no base route found, allow access (for paths not in menu system)
        return true;
    }

    /**
     * Get breadcrumb for a path
     */
    getBreadcrumb(path, userType) {
        const items = allMenuItems[userType] || [];
        const item = items.find(item => item.path === path);
        
        if (!item) {
            return [];
        }

        const breadcrumb = [
            { text: 'Dashboard', path: `/${userType}/dashboard` }
        ];

        if (item.path !== `/${userType}/dashboard`) {
            breadcrumb.push({ text: item.text, path: item.path });
        }

        return breadcrumb;
    }

    /**
     * Get all permissions for a user type
     */
    getAllPermissions(userType) {
        const items = allMenuItems[userType] || [];
        return [...new Set(items.map(item => item.permission).filter(Boolean))];
    }
}

const menuService = new MenuService();
export default menuService; 